import { useCallback, useEffect, useState } from 'react';
import { AppLayout, Button, ErrorBanner, Spinner } from '@scoring/frontend-shared';
import type { ApiClient } from '@scoring/frontend-shared';
import { useApiClient } from '../hooks/useApiClient.js';

/**
 * Shape of a station queue entry from `GET /events/:eventId/stations/queue`.
 */
interface StationQueueEntry {
  stationNumber: number;
  teamsWaiting: number;
  teamsHeading: number;
}

/**
 * Shape of a station status entry from `GET /events/:eventId/stations/status`.
 */
interface StationStatusEntry {
  stationNumber: number;
  completed: boolean;
  checkedIn: boolean;
  isNextStation: boolean;
}

/**
 * API response shape for the participant's current event.
 */
interface ParticipantEvent {
  eventId: string;
  eventName: string;
  eventStatus: string;
  teamName: string | null;
  eventScopedTeamId: string | null;
  teamPublicId: string | null;
}

/**
 * Stations page — displays station queue counts, check-in functionality,
 * and next-station navigation declaration.
 *
 * Requirements:
 *   - 14.1: check-in at station for the team
 *   - 14.3: declare next intended station via Station_Navigation
 *   - 25.1: display current number of teams checked in at each station
 *   - 25.2: update displayed station queue counts
 *   - 14.4: display which stations are completed and which remain
 */
export function StationsPage(): JSX.Element {
  const api = useApiClient();

  const [currentEvent, setCurrentEvent] = useState<ParticipantEvent | null>(null);
  const [queueData, setQueueData] = useState<StationQueueEntry[]>([]);
  const [stationStatus, setStationStatus] = useState<StationStatusEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkInProgress, setCheckInProgress] = useState<number | null>(null);
  const [navInProgress, setNavInProgress] = useState<number | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const fetchData = useCallback(
    async (client: ApiClient): Promise<void> => {
      try {
        setLoading(true);
        setError(null);

        // First, get the participant's current event
        const eventsData = await client.request<{ events: ParticipantEvent[] }>('GET', '/account/events');
        const events = eventsData.events;
        const activeEvent = events.find((e) => e.eventStatus === 'active');

        if (!activeEvent) {
          setCurrentEvent(null);
          setLoading(false);
          return;
        }

        setCurrentEvent(activeEvent);

        // Fetch station queue and (team-scoped) status in parallel.
        const [queue, status] = await Promise.all([
          client.request<{ stations: StationQueueEntry[] }>(
            'GET',
            `/events/${encodeURIComponent(activeEvent.eventId)}/stations/queue`,
          ),
          activeEvent.teamPublicId
            ? client.request<{ stations: StationStatusEntry[] }>(
                'GET',
                `/events/${encodeURIComponent(activeEvent.eventId)}/stations/status`,
                { query: { teamId: activeEvent.teamPublicId } },
              )
            : Promise.resolve({ stations: [] as StationStatusEntry[] }),
        ]);

        setQueueData(queue.stations);
        setStationStatus(status.stations);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to load station data',
        );
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;

    void fetchData(api).then(() => {
      if (cancelled) return;
    });

    return () => {
      cancelled = true;
    };
  }, [api, fetchData]);

  async function handleCheckIn(stationNumber: number): Promise<void> {
    if (!currentEvent) return;
    if (!currentEvent.teamPublicId) {
      setError('You are not assigned to a team for this event.');
      return;
    }

    try {
      setCheckInProgress(stationNumber);
      setError(null);
      setSuccessMessage(null);

      await api.request<void>(
        'POST',
        `/events/${encodeURIComponent(currentEvent.eventId)}/checkins`,
        { body: { teamId: currentEvent.teamPublicId, stationId: stationNumber } },
      );

      setSuccessMessage(`Checked in at Station ${stationNumber}`);

      // Update local station status
      setStationStatus((prev) =>
        prev.map((s) =>
          s.stationNumber === stationNumber ? { ...s, checkedIn: true } : s,
        ),
      );

      // Refresh queue data
      const updatedQueue = await api.request<{ stations: StationQueueEntry[] }>(
        'GET',
        `/events/${encodeURIComponent(currentEvent.eventId)}/stations/queue`,
      );
      setQueueData(updatedQueue.stations);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to check in',
      );
    } finally {
      setCheckInProgress(null);
    }
  }

  async function handleDeclareNext(stationNumber: number): Promise<void> {
    if (!currentEvent) return;
    if (!currentEvent.teamPublicId) {
      setError('You are not assigned to a team for this event.');
      return;
    }

    try {
      setNavInProgress(stationNumber);
      setError(null);
      setSuccessMessage(null);

      await api.request<void>(
        'POST',
        `/events/${encodeURIComponent(currentEvent.eventId)}/navigation`,
        { body: { teamId: currentEvent.teamPublicId, nextStation: stationNumber } },
      );

      setSuccessMessage(`Declared next station: Station ${stationNumber}`);

      // Reflect the declared next station immediately (a team has one current
      // next station, so clear it from the others).
      setStationStatus((prev) =>
        prev.map((s) => ({ ...s, isNextStation: s.stationNumber === stationNumber })),
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to declare next station',
      );
    } finally {
      setNavInProgress(null);
    }
  }

  async function refreshQueue(): Promise<void> {
    if (!currentEvent) return;
    const updated = await api.request<{ stations: StationQueueEntry[] }>(
      'GET',
      `/events/${encodeURIComponent(currentEvent.eventId)}/stations/queue`,
    );
    setQueueData(updated.stations);
  }

  async function handleCancelCheckIn(stationNumber: number): Promise<void> {
    if (!currentEvent?.teamPublicId) return;
    try {
      setCheckInProgress(stationNumber);
      setError(null);
      setSuccessMessage(null);
      await api.request<void>('DELETE', `/events/${encodeURIComponent(currentEvent.eventId)}/checkins`, {
        query: { teamId: currentEvent.teamPublicId, stationId: stationNumber },
      });
      setSuccessMessage(`Cancelled check-in at Station ${stationNumber}`);
      setStationStatus((prev) =>
        prev.map((s) => (s.stationNumber === stationNumber ? { ...s, checkedIn: false } : s)),
      );
      await refreshQueue();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel check-in');
    } finally {
      setCheckInProgress(null);
    }
  }

  async function handleCancelNext(): Promise<void> {
    if (!currentEvent?.teamPublicId) return;
    try {
      setNavInProgress(-1);
      setError(null);
      setSuccessMessage(null);
      await api.request<void>('DELETE', `/events/${encodeURIComponent(currentEvent.eventId)}/navigation`, {
        query: { teamId: currentEvent.teamPublicId },
      });
      setSuccessMessage('Cancelled next station');
      setStationStatus((prev) => prev.map((s) => ({ ...s, isNextStation: false })));
      await refreshQueue();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel next station');
    } finally {
      setNavInProgress(null);
    }
  }

  // Station state machine: a team is "checked in" at one active station, or
  // "heading" to a declared next station, or idle. Completed stations are done.
  const activeCheckIn = stationStatus.find((s) => s.checkedIn && !s.completed) ?? null;
  const headingStation = activeCheckIn
    ? null
    : stationStatus.find((s) => s.isNextStation && !s.completed) ?? null;

  return (
    <AppLayout title="Stations">
      {error && (
        <ErrorBanner onDismiss={() => setError(null)}>
          {error}
        </ErrorBanner>
      )}

      {successMessage && (
        <div
          role="status"
          style={{
            padding: '0.75rem 1rem',
            borderRadius: 'var(--radius-button, 8px)',
            border: '1px solid var(--color-brand-green, #1B4332)',
            background: '#e6f4ea',
            color: 'var(--color-brand-green, #1B4332)',
            marginBottom: '1rem',
          }}
        >
          {successMessage}
        </div>
      )}

      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <Spinner label="Loading station data…" size="1.5rem" />
        </div>
      )}

      {!loading && !currentEvent && (
        <p style={{ color: 'var(--color-text-secondary, #6B7280)', textAlign: 'center', padding: '2rem 0' }}>
          No active event found. Stations are available during active events only.
        </p>
      )}

      {!loading && currentEvent && (
        <>
          {/* Station Queue Display (Req 25.1, 25.2) */}
          <section aria-label="Station queue">
            <h2 style={{ fontSize: 'var(--font-size-heading-sm, 1.25rem)', marginBottom: '0.75rem' }}>
              Station Queue
            </h2>
            <p style={{ fontSize: 'var(--font-size-body-sm, 0.875rem)', color: 'var(--color-text-secondary, #6B7280)', marginBottom: '0.75rem' }}>
              Teams waiting at each station
            </p>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                gap: '0.5rem',
                marginBottom: '1.5rem',
              }}
            >
              {queueData.map((station) => {
                const status = stationStatus.find(
                  (s) => s.stationNumber === station.stationNumber,
                );
                const isCompleted = status?.completed ?? false;

                return (
                  <div
                    key={station.stationNumber}
                    style={{
                      border: '1px solid var(--color-border, #E5E7EB)',
                      borderRadius: 'var(--radius-card, 12px)',
                      padding: '0.75rem',
                      textAlign: 'center',
                      backgroundColor: isCompleted ? '#e6f4ea' : 'var(--color-bg-surface, #FFFFFF)',
                    }}
                  >
                    <div style={{ fontSize: 'var(--font-size-body-sm, 0.875rem)', color: 'var(--color-text-secondary, #6B7280)' }}>
                      Station {station.stationNumber}
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>
                      {station.teamsWaiting}
                    </div>
                    <div style={{ fontSize: 'var(--font-size-body-sm, 0.875rem)', color: 'var(--color-text-secondary, #6B7280)' }}>
                      {station.teamsWaiting === 1 ? 'team waiting' : 'teams waiting'}
                    </div>
                    {station.teamsHeading > 0 && (
                      <div style={{ fontSize: 'var(--font-size-body-sm, 0.875rem)', color: 'var(--color-brand-orange, #F48C06)', fontWeight: 500, marginTop: '0.25rem' }}>
                        → {station.teamsHeading} heading here
                      </div>
                    )}
                    {isCompleted && (
                      <span
                        style={{
                          display: 'inline-block',
                          marginTop: '0.25rem',
                          fontSize: 'var(--font-size-body-sm, 0.875rem)',
                          color: 'var(--color-brand-green, #1B4332)',
                          fontWeight: 500,
                        }}
                      >
                        ✓ Done
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* Check-In and Navigation (Req 14.1, 14.3, 14.4) */}
          <section aria-label="Station check-in and navigation">
            <h2 style={{ fontSize: 'var(--font-size-heading-sm, 1.25rem)', marginBottom: '0.75rem' }}>
              Check-In &amp; Navigate
            </h2>
            <p style={{ fontSize: 'var(--font-size-body-sm, 0.875rem)', color: 'var(--color-text-secondary, #6B7280)', marginBottom: '0.75rem' }}>
              Check in at your current station or declare your next station
            </p>
            <ul
              style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '0.5rem' }}
              aria-label="Station actions"
            >
              {stationStatus.map((station) => (
                <li
                  key={station.stationNumber}
                  style={{
                    border: '1px solid var(--color-border, #E5E7EB)',
                    borderRadius: 'var(--radius-card, 12px)',
                    padding: '0.75rem 1rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: station.completed ? '#f8faf8' : 'var(--color-bg-surface, #FFFFFF)',
                  }}
                >
                  <div>
                    <strong>Station {station.stationNumber}</strong>
                    {station.completed && (
                      <span
                        style={{
                          marginLeft: '0.5rem',
                          fontSize: 'var(--font-size-body-sm, 0.875rem)',
                          color: 'var(--color-brand-green, #1B4332)',
                          fontWeight: 500,
                        }}
                      >
                        Completed
                      </span>
                    )}
                    {station.checkedIn && !station.completed && (
                      <span
                        style={{
                          marginLeft: '0.5rem',
                          fontSize: 'var(--font-size-body-sm, 0.875rem)',
                          color: '#0969da',
                          fontWeight: 500,
                        }}
                      >
                        Checked In
                      </span>
                    )}
                    {station.isNextStation && !station.completed && !station.checkedIn && (
                      <span
                        style={{
                          marginLeft: '0.5rem',
                          fontSize: 'var(--font-size-body-sm, 0.875rem)',
                          color: 'var(--color-brand-orange, #F48C06)',
                          fontWeight: 500,
                        }}
                      >
                        → Your next station
                      </span>
                    )}
                  </div>
                  {!station.completed && (
                    <div style={{ display: 'flex', gap: '0.375rem' }}>
                      {/* Checked in here → only allow cancelling the check-in. */}
                      {activeCheckIn?.stationNumber === station.stationNumber && (
                        <Button
                          variant="secondary"
                          disabled={checkInProgress === station.stationNumber}
                          onClick={() => void handleCancelCheckIn(station.stationNumber)}
                          aria-label={`Cancel check-in at Station ${station.stationNumber}`}
                          style={{ fontSize: 'var(--font-size-body-sm, 0.875rem)', padding: '0.375rem 0.625rem' }}
                        >
                          {checkInProgress === station.stationNumber ? '…' : 'Cancel Check-In'}
                        </Button>
                      )}
                      {/* Check in: allowed when not checked in anywhere, and — if
                          heading somewhere — only at that declared station. */}
                      {!activeCheckIn &&
                        (!headingStation || headingStation.stationNumber === station.stationNumber) && (
                          <Button
                            variant="primary"
                            disabled={checkInProgress === station.stationNumber}
                            onClick={() => void handleCheckIn(station.stationNumber)}
                            aria-label={`Check in at Station ${station.stationNumber}`}
                            style={{ fontSize: 'var(--font-size-body-sm, 0.875rem)', padding: '0.375rem 0.625rem' }}
                          >
                            {checkInProgress === station.stationNumber ? '…' : 'Check In'}
                          </Button>
                        )}
                      {/* Heading here → allow cancelling the declared next station. */}
                      {headingStation?.stationNumber === station.stationNumber && (
                        <Button
                          variant="secondary"
                          disabled={navInProgress !== null}
                          onClick={() => void handleCancelNext()}
                          aria-label="Cancel next station"
                          style={{ fontSize: 'var(--font-size-body-sm, 0.875rem)', padding: '0.375rem 0.625rem' }}
                        >
                          {navInProgress === -1 ? '…' : 'Cancel Next'}
                        </Button>
                      )}
                      {/* Go next: only when idle (not checked in, not heading). */}
                      {!activeCheckIn && !headingStation && (
                        <Button
                          variant="secondary"
                          disabled={navInProgress === station.stationNumber}
                          onClick={() => void handleDeclareNext(station.stationNumber)}
                          aria-label={`Declare Station ${station.stationNumber} as next`}
                          style={{ fontSize: 'var(--font-size-body-sm, 0.875rem)', padding: '0.375rem 0.625rem' }}
                        >
                          {navInProgress === station.stationNumber ? '…' : 'Go Next'}
                        </Button>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </section>
        </>
      )}
    </AppLayout>
  );
}
